import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BonLivraison } from "app/features/bon-livraison/models/bon-livraison.model";
import { Chargement } from "app/features/bon-livraison/models/chargement.model";
import { TypePdf } from "app/features/documents/enums/type-pdf.enum";
import { LotMouvement } from "app/features/lot/models/lot.model";
import { Palette } from "app/features/palette/models/palette.model";
import { format } from "date-fns";
import { printPdf } from "../../../shared/utils/print-pdf.utils";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { DialogsService } from "../../../shared/services/dialogs/dialogs.service";
import { SnackService } from "../../../shared/services/snack-service.service";
import { CommandeClientEtat } from "../enums/etat-commande-client.enum";
import { CommandeClientLigne } from "../models/commande-client-ligne";
import { CommandeClient } from "../models/commande-client.model";
import { Facture } from "app/features/facture/models/facture.model";

@Injectable({
  providedIn: "root",
})
export class CommandeClientsService {
  private _commandeClients$ = new BehaviorSubject<CommandeClient[]>([]);
  private _commandeClientSaving$ = new BehaviorSubject<boolean>(false);
  private _commandeClientSaved$ = new BehaviorSubject<boolean>(false);

  private _currentCommandeClient$ = new BehaviorSubject<CommandeClient>(null);

  get commandeClients$(): Observable<CommandeClient[]> {
    return this._commandeClients$.asObservable();
  }

  get currentCommandeClient$(): Observable<CommandeClient> {
    return this._currentCommandeClient$.asObservable();
  }

  get commandeClientSaving$(): Observable<boolean> {
    return this._commandeClientSaving$.asObservable();
  }

  get commandeClientSaved$(): Observable<boolean> {
    return this._commandeClientSaved$.asObservable();
  }

  resetState() {
    this._commandeClientSaving$.next(false);
    this._commandeClientSaved$.next(false);
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private snackService: SnackService,
    private dialogs: DialogsService,
  ) {}

  loadAll(): void {
    this.http
      .get<CommandeClient[]>(`${environment.api()}/commandes_client`)
      .subscribe((commandeClients) => {
        this._commandeClients$.next(commandeClients);
      });
  }

  loadAllReadyForPreparation(adresseLivraisonId: string): void {
    this.http
      .get<
        CommandeClient[]
      >(`${environment.api()}/commandes_client/ready_for_preparation/adresseLivraisonId/${adresseLivraisonId}`)
      .subscribe((commandeClients) => {
        this._commandeClients$.next(commandeClients);
      });
  }

  loadAllNotComplete(): void {
    this.http
      .get<
        CommandeClient[]
      >(`${environment.api()}/commandes_client/not_complete`)
      .subscribe((commandeClients) => {
        this._commandeClients$.next(commandeClients);
      });
  }

  setCurrent(a: CommandeClient): void {
    this._currentCommandeClient$.next(a);
  }

  loadCommandeEager(a: CommandeClient): void {
    this.http
      .get<CommandeClient>(`${environment.api()}/commandes_client/id/${a.id}`)
      .subscribe((commande) => {
        this._currentCommandeClient$.next(commande);
      });
  }

  loadCommandeClientHttp(a: CommandeClient) {
    return this.http.get<CommandeClient>(
      `${environment.api()}/commandes_client/id/${a.id}`,
    );
  }

  setCurrentWithId(id: string) {
    let found = null;
    this.commandeClients$
      .subscribe((as) => {
        found = as.find((a) => a.id === id);
      })
      .unsubscribe();
    this.setCurrent(found);
  }

  showDetails(a: CommandeClient): void {
    this.setCurrent(a);
    this.router.navigate(["/parametrage-commandeClient"]);
  }

  delete(a: CommandeClient): void {
    this._commandeClientSaving$.next(true);
    this.http
      .delete(`${environment.api()}/commandes_client/id/${a.id}`)
      .subscribe((deletedCommandeClient: CommandeClient) => {
        this._commandeClientSaving$.next(false);
        this.loadAll();
        //this.refreshFor(savedCommandeClient, "delete");
        this.snackService.success(`Commande client supprimée`);
      });
  }

  save(a: CommandeClient): void {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);
    this.http
      .post<CommandeClient>(`${environment.api()}/commandes_client`, a)
      .subscribe((savedCommandeClient: CommandeClient) => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);
        this.setCurrent(savedCommandeClient);
        this.loadAll();
        //this.refreshFor(savedCommandeClient, "save");
        this.snackService.success(`Commande client enregistrée`);
      });
  }

  preparer(a: CommandeClient): void {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);
    this.http
      .post<CommandeClient>(`${environment.api()}/commandes_client/preparer`, a)
      .subscribe((savedCommande: CommandeClient) => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);
        this.setCurrent(savedCommande);
        this.loadAll();
        this.snackService.success(`Commande en cours de préparation`);
        this._commandeClientSaved$.next(false);
      });
  }

  changeEtat(a: CommandeClient, etat: CommandeClientEtat): void {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);

    this.http
      .post<CommandeClient>(
        `${environment.api()}/commandes_client/etat/` + etat,
        a,
      )
      .subscribe((savedCommande: CommandeClient) => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);
        this.setCurrent(savedCommande);
        this.loadAll();
        this.snackService.success(`Commande annulée`);
        this._commandeClientSaved$.next(false);
      });
  }

  expedier(a: CommandeClient): void {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);

    this.http
      .post<CommandeClient>(
        `${environment.api()}/expedition_commande/expedier`,
        a,
      )
      .subscribe((savedCommande: CommandeClient) => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);
        this._commandeClientSaved$.next(false);
        this.setCurrent(savedCommande);
        this.loadAll();
        this.snackService.success(`Commande expediée`);
      });
  }

  expedierPalettes(palettes: Palette[]): void {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);

    this.http
      .post<CommandeClient>(
        `${environment.api()}/expedition_commande/expedier/palettes`,
        { palettes },
      )
      .subscribe((savedCommande: CommandeClient) => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);
        this.snackService.success(
          `${palettes.length} palette${palettes.length > 1 ? "s" : ""} expediée${palettes.length > 1 ? "s" : ""}`,
        );
        this._commandeClientSaved$.next(false);
      });
  }

  expedierBonLivraisons(bls: BonLivraison[]): void {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);

    this.http
      .post<CommandeClient>(
        `${environment.api()}/expedition_commande/expedier/bls`,
        { bls },
      )
      .subscribe((savedCommande: CommandeClient) => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);

        let nbPalettes = 0;
        bls.forEach((bl) => (nbPalettes += bl.palettes.length));

        this.snackService.success(
          `${nbPalettes} palette${nbPalettes > 1 ? "s" : ""} expediée${nbPalettes > 1 ? "s" : ""}`,
        );
        this._commandeClientSaved$.next(false);
      });
  }

  async expedierChargement(c: Chargement) {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);

    this.http
      .post<CommandeClient>(`${environment.api()}/chargements/expedier/`, { c })
      .subscribe(() => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);

        localStorage.removeItem("CHARGEMENT" + c.id);
        let nbPalettes = 0;
        c.bls.forEach((bl) => (nbPalettes += bl.palettes.length));

        this.snackService.success(
          `${nbPalettes} palette${nbPalettes > 1 ? "s" : ""} expediée${nbPalettes > 1 ? "s" : ""}`,
        );
        this._commandeClientSaved$.next(false);
        localStorage.removeItem("CHARGEMENT" + c.id);
      });
  }

  expedierSansExpedition(mouvements: LotMouvement[]): void {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);

    this.http
      .post<CommandeClient>(
        `${environment.api()}/expedition_commande/expedier/sans_expedition`,
        { mouvements },
      )
      .subscribe(() => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);

        this.snackService.success(`Livraison sans expédition réalisée`);
        this._commandeClientSaved$.next(false);
      });
  }

  expedierPrestation(payload: {
    commandeClientLigne: CommandeClientLigne;
    commandeClient: CommandeClient;
    quantite: number;
    prixUnitaire: number;
  }): void {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);

    this.http
      .post<CommandeClient>(
        `${environment.api()}/expedition_commande/expedier/prestation`,
        payload,
      )
      .subscribe(() => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);

        this.snackService.success(`Prestation réalisée`);
        this._commandeClientSaved$.next(false);
      });
  }

  confirm(a: CommandeClient): void {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);
    this.http
      .post<CommandeClient>(
        `${environment.api()}/commandes_client/confirmer`,
        a,
      )
      .subscribe((savedCommande: CommandeClient) => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);
        this.setCurrent(savedCommande);
        this.loadAll();
        this.snackService.success(`Commande confirmée`);
        this._commandeClientSaved$.next(false);
      });
  }

  terminer(a: CommandeClient): void {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);
    this.http
      .post<CommandeClient>(`${environment.api()}/commandes_client/terminer`, a)
      .subscribe((savedCommande: CommandeClient) => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);
        this.setCurrent(savedCommande);
        this.loadAll();
        this.snackService.success(`Commande terminée`);
        this._commandeClientSaved$.next(false);
      });
  }

  /**
   * Phase 4 : rouvre une CC TERMINEE en la repassant en EN_COURS_DE_PREPARATION.
   * Utilisé par le bouton bidirectionnel "Remettre en cours" dans l'aside.
   */
  reopen(a: CommandeClient): void {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);
    this.http
      .post<CommandeClient>(`${environment.api()}/commandes_client/reopen`, a)
      .subscribe((savedCommande: CommandeClient) => {
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);
        this.setCurrent(savedCommande);
        this.loadAll();
        this.snackService.success(`Commande remise en cours`);
        this._commandeClientSaved$.next(false);
      });
  }

  async getPdf(linkId: string) {
    await this.dialogs.openPdfLoader(async () => {
      const payload = await firstValueFrom(
        this.http.get(
          `${environment.api()}/expedition_commande/payload/${linkId}`,
        ),
      );
      const res = await firstValueFrom(
        this.http.post(
          `${environment.pdfApi()}/pdf/${TypePdf.BL_CLIENT}`,
          payload,
          {
            responseType: "blob",
          },
        ),
      );
      const file = new Blob([res], { type: "application/pdf" });
      const blNumero = (payload as any)?.numeroBL ? (payload as any).numeroBL.replace(/-/g, '') : 'BL';
      const blTiers = (payload as any)?.commandeClient?.tiers?.denomination || '';
      const title = blTiers ? `${blNumero} - ${blTiers}` : blNumero;
      printPdf(URL.createObjectURL(file), title);
    });
  }

  getDefaultMailForModification(c: CommandeClient) {
    return `<p>Bonjour,</p><p>Il y a eu une modification pour votre commande ${c.numeroCommandeClient}.</p><p>Vous trouverez ci-jointe la confirmation correspondante pour une livraison prévue le ${format(new Date(c.dateLivraisonPrevue), "dd/MM/yyyy")} </p><p>Merci de bien vouloir vérifier les différents éléments de cette confirmation et de revenir vers nous si besoin.</p><p>Nous restons à votre entière disposition,</p>`;
  }

  getDefaultMailFor(c: CommandeClient) {
    return `<p>Bonjour,</p><p>Nous avons bien reçu votre commande ${c.numeroCommandeClient} et vous en remercions.</p><p>Vous trouverez ci-jointe la confirmation correspondante pour une livraison prévue le ${format(new Date(c.dateLivraisonPrevue), "dd/MM/yyyy")} </p><p>Merci de bien vouloir vérifier les différents éléments de cette confirmation et de revenir vers nous si besoin.</p><p>Nous restons à votre entière disposition,</p>`;
  }

  getDefaultMailSubjectFor(c: CommandeClient) {
    return `CONFIRMATION COMMANDE ${c.numero}${c.numeroCommandeClient ? ` (${c.numeroCommandeClient})` : ""} - POUR LIVRAISON LE ${format(new Date(c.dateLivraisonPrevue), "dd/MM/yyyy")}`;
  }

  getDefaultMailSubjectForModification(c: CommandeClient) {
    return `CONFIRMATION COMMANDE ${c.numero}${c.numeroCommandeClient ? ` (${c.numeroCommandeClient})` : ""} (MODIFICATION) - POUR LIVRAISON LE ${format(new Date(c.dateLivraisonPrevue), "dd/MM/yyyy")}`;
  }

  getDefaultMailForFactureRelance(facture: Facture) {
    const isAvoir = (facture as any)?.isAvoir === true;
    const due = !isAvoir ? (facture as any)?.dateEcheance : null;
    const dueStr = due ? format(new Date(due), "dd/MM/yyyy") : null;

    if (isAvoir) {
      return `<p>Bonjour,</p>
<p>Suite à l’avoir ${facture.numero ?? ""}, vous trouverez ci-joint un duplicata.</p>
<p>Merci de nous indiquer si vous souhaitez son <b>imputation</b> sur une prochaine facture ou un <b>remboursement</b> (le cas échéant, merci de nous transmettre/valider votre RIB).</p>
<p>Nous restons à votre entière disposition,</p>`;
    }

    return `<p>Bonjour,</p>
<p>Sauf erreur de notre part, la facture ${facture.numero ?? ""}${dueStr ? ` arrivée à échéance le ${dueStr}` : ""} reste en attente de règlement.</p>
<p>Vous trouverez ci-joint le duplicata de la facture correspondante. Nous vous remercions par avance de procéder au règlement dans les meilleurs délais${dueStr ? `, idéalement avant le ${dueStr}` : ""}.</p>
<p>Si le paiement a déjà été effectué, merci d’ignorer ce message et de nous en informer le cas échéant.</p>
<p>Nous restons à votre entière disposition,</p>`;
  }

  getDefaultMailSubjectForFactureRelance(facture: Facture) {
    const isAvoir = (facture as any)?.isAvoir === true;
    const due = !isAvoir ? (facture as any)?.dateEcheance : null;
    return `${isAvoir ? "SUIVI AVOIR" : "RELANCE FACTURE"} ${facture.numero ?? ""}${
      due ? ` - ÉCHÉANCE AU ${format(new Date(due), "dd/MM/yyyy")}` : ""
    }`;
  }

  getDefaultMailForFacture(facture: Facture) {
    const isAvoir = (facture as any)?.isAvoir === true;
    const dateFacture = (facture as any)?.dateFacture ?? (facture as any)?.date;
    const dateStr = dateFacture
      ? format(new Date(dateFacture), "dd/MM/yyyy")
      : null;

    if (isAvoir) {
      return `<p>Bonjour,</p>
<p>Veuillez trouver ci-joint votre <b>avoir</b> ${facture.numero ?? ""}${dateStr ? ` daté du ${dateStr}` : ""}.</p>
<p>Ce montant pourra être <b>imputé</b> sur vos prochaines factures ou faire l’objet d’un <b>remboursement</b> sur demande.</p>
<p>Nous restons à votre entière disposition pour toute question,</p>`;
    }

    const due = (facture as any)?.dateEcheance;
    const dueStr = due ? format(new Date(due), "dd/MM/yyyy") : null;

    return `<p>Bonjour,</p>
<p>Veuillez trouver ci-joint votre facture ${facture.numero ?? ""}${dateStr ? ` datée du ${dateStr}` : ""}.</p>
<p>${dueStr ? `La date d’échéance est fixée au ${dueStr}. ` : ""}Vous remerciant par avance de votre aimable règlement.</p>
<p>Nous restons à votre entière disposition pour toute question,</p>`;
  }

  getDefaultMailSubjectForFacture(facture: Facture) {
    const isAvoir = (facture as any)?.isAvoir === true;
    const dateFacture = (facture as any)?.dateFacture ?? (facture as any)?.date;
    return `${isAvoir ? "AVOIR" : "FACTURE"} ${facture.numero ?? ""}${
      dateFacture ? ` - DU ${format(new Date(dateFacture), "dd/MM/yyyy")}` : ""
    }`;
  }

  async getPdfConfirmation(c: CommandeClient, provisoire: boolean, ribId?: string) {
    await this.dialogs.openPdfLoader(async () => {
      const url = `${environment.api()}/commandes_client/commande_pdf/payload/${c.id}/${provisoire}`;
      const payload = await firstValueFrom(
        this.http.get(ribId ? `${url}?ribId=${ribId}` : url),
      );
      const res = await firstValueFrom(
        this.http.post(
          `${environment.pdfApi()}/pdf/${TypePdf.CONFIRMATION_COMMANDE_CLIENT}`,
          payload,
          {
            responseType: "blob",
          },
        ),
      );
      const file = new Blob([res], { type: "application/pdf" });
      const numero = c.numero ? c.numero.replace(/-/g, '') : '';
      const tiers = c.tiers?.denomination || '';
      const title = provisoire
        ? `PROFORMA${numero ? ` ${numero}` : ''} - ${tiers}`
        : `${numero || 'Confirmation'} - ${tiers}`;
      printPdf(URL.createObjectURL(file), title);
    });
  }

  saveLigne(commandeClientLigne: CommandeClientLigne) {
    this._commandeClientSaving$.next(true);
    this._commandeClientSaved$.next(false);
    this.http
      .post<CommandeClientLigne>(
        `${environment.api()}/commandes_client/ligne/update`,
        commandeClientLigne,
      )
      .subscribe((savedCommandeClientLigne) => {
        this.snackService.success(`Informations enregistrées`);
        this._commandeClientSaving$.next(false);
        this._commandeClientSaved$.next(true);
        this._commandeClientSaved$.next(false);
        this.loadCommandeEager({
          id: commandeClientLigne.commandeClientId,
        } as CommandeClient);
      });
  }
}
